import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import PathWizard from "../Path/PathWizard";
import { usePathDetailV2 } from "../Path/hooks/usePathDetailV2";
import { useEditPathV2 } from "../Path/hooks/useEditPathV2";
import { useSubmitPathV2 } from "../Path/hooks/useSubmitPathV2";
import { PathWizardData } from "../Path/types";

interface EditPathProps {
  duplication?: boolean;
}

const EditPath: React.FC<EditPathProps> = ({ duplication = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("wizard-path");
  const { id: corporateId = "", idPath = "" } = useParams();
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const [successOpen, setSuccessOpen] = useState(false);

  const {
    wizardData,
    initialInitiativeIds,
    initialEditorial,
    isLoading,
    error,
    mutate,
  } = usePathDetailV2({ corporateId, idPath });

  const { submit: submitEdit, isSubmitting: isSubmittingEdit } = useEditPathV2({
    corporateId,
    loId: Number(idPath),
    initialInitiativeIds,
    initialEditorial,
  });

  const { submit: submitAdd, isSubmitting: isSubmittingAdd } = useSubmitPathV2({
    corporateId,
  });

  const isSubmitting = duplication ? isSubmittingAdd : isSubmittingEdit;

  const initialData = useMemo<PathWizardData | null>(() => {
    if (!wizardData) return null;
    if (!duplication) return wizardData;
    return {
      ...wizardData,
      title: `${wizardData.title || ""} - Copied`,
      publish_date: null,
    };
  }, [wizardData, duplication]);

  const handleComplete = async (data: PathWizardData) => {
    try {
      if (duplication) {
        await submitAdd(data);
      } else {
        await submitEdit(data);
      }
      setSuccessOpen(true);
    } catch (err) {
      console.error(
        duplication
          ? "[path-wizard] duplicate failed"
          : "[path-wizard] edit failed",
        err,
      );
      throw err;
    }
  };

  const handleDismissSuccess = () => {
    if (!duplication) void mutate();
    navigate(catalogUrl);
  };

  if (isLoading || (!initialData && !error)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !initialData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("edit-load-error.title") || "Impossibile caricare il percorso"}
        </Typography>
        <Typography sx={{ fontSize: "13px" }}>
          {error || t("edit-load-error.description") || ""}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PathWizard
        initialData={initialData}
        onComplete={handleComplete}
        isSubmitting={isSubmitting}
        mode={duplication ? "add" : "edit"}
      />
      <LOOutcomeModalCst
        open={successOpen}
        entityType="path"
        mode={duplication ? "add" : "edit"}
        onDismiss={handleDismissSuccess}
      />
    </>
  );
};

export default EditPath;
